import React, { useState, useEffect } from 'react';
import WatermarkedImage from '../WatermarkedImage';
import { useCart } from '../../context/CartContext';

const ArtworkCarousel = () => {
  const [images, setImages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalActiveIndex, setModalActiveIndex] = useState(0);
  const [selectedFrame, setSelectedFrame] = useState(null);
  
  // ← Use your existing cart function
  const { addToCart } = useCart();

  const frames = {
    blue: '/frames/blueframe.png',
    black: '/frames/blackframe.png',
    gold: '/frames/goldframe.png',
    redwood: '/frames/redwoodframe.png'
  };

  // ================= FETCH ALL IMAGES =================
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const res = await fetch('/api/artupImages');
        const data = await res.json();
        setImages(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchImages();
  }, []);

  // ================= AUTO SLIDE =================
  useEffect(() => {
    if (!images.length) return;

    const interval = setInterval(() => {
      setActiveIndex((i) => (i + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  // ================= NAV FOR MAIN CAROUSEL =================
  const showSlide = (index) => {
    if (!images.length) return;

    if (index < 0) index = images.length - 1;
    if (index >= images.length) index = 0;

    setActiveIndex(index);
  };

  // ================= OPEN FRAME MODAL CAROUSEL =================
  const openFrameModal = async (frameName) => {
    setSelectedFrame(frameName);
    setModalActiveIndex(0);
    
    try {
      const response = await fetch(`/api/artupImages?frame=${frameName}`);
      const frameArtwork = await response.json();
      
      setModalImages(frameArtwork);
      setIsModalOpen(true);
    } catch (err) {
      console.error(`Error loading ${frameName} artwork:`, err);
    }
  };

  // ================= MODAL NAVIGATION =================
  const showModalSlide = (direction) => {
    let newIndex = modalActiveIndex + direction;
    
    if (newIndex < 0) newIndex = modalImages.length - 1;
    if (newIndex >= modalImages.length) newIndex = 0;
    
    setModalActiveIndex(newIndex);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImages([]);
    setModalActiveIndex(0);
    setSelectedFrame(null);
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      if (e.key === 'ArrowLeft') {
        showModalSlide(-1);
      } else if (e.key === 'ArrowRight') {
        showModalSlide(1);
      } else if (e.key === 'Escape') {
        closeModal();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, modalActiveIndex, modalImages.length]);

  // ← Handle add to cart - converts artwork format to match your cart system
const handleAddToCart = (artwork) => {
  if (!artwork) return;
  
  const cartProduct = {
    id: artwork.id,
    name: artwork.title,
    price: Math.round(artwork.price * 100),
    image: artwork.image,
  };
  
  addToCart(cartProduct);
  
  // Close modal if open
  if (isModalOpen) {
    closeModal();
  }
};

  const activeImage = images[activeIndex];
  const currentModalImage = modalImages[modalActiveIndex];

  if (!images.length) return <div>Loading artworks...</div>;

  return (
    <section id="art" className="block-container" style={{ textAlign: 'center', color: '#fff' }}>

      <h2></h2>

      {/* ================= MAIN CAROUSEL ================= */}
      <div
        className="artwork-carousel"
        style={{ position: 'relative', width: '95%', margin: '-0rem auto' }}
      >

        {/* PREV */}
        <button
          onClick={() => showSlide(activeIndex - 1)}
          style={navBtnStyle('left')}
        >
          ❮
        </button>

        {/* MAIN LAYOUT */}
        <div
          className="artwork-track"
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            alignItems: 'flex-start'
          }}
        >

          {/* LEFT: IMAGE */}
          <div style={{ position: 'relative', width: '60%' }}>

            <div style={{ position: 'relative', display: 'inline-block', width: '90%' }}>

              <WatermarkedImage
                src={typeof activeImage === 'string' ? activeImage : activeImage?.image}
                watermark=""
                style={{ width: '100%', borderRadius: '12px' }}
              />

            
            </div>
            
            {/* ← ADD TO CART BUTTON for main carousel */}
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <button
                onClick={() => handleAddToCart(activeImage)}
                className="add-to-cart-btn"
                style={addToCartBtnStyle}
              >
                🛒 Add to Cart - ${activeImage?.price || '0'}
              </button>
            </div>
          </div>

          {/* RIGHT: FRAME THUMBNAILS */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.keys(frames).map((frameName) => (
              <div
                key={frameName}
                onClick={() => openFrameModal(frameName)}
                style={{
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                <img
                  src={frames[frameName]}
                  alt={frameName}
                  style={{
                    width: '65px',
                    height: '65px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    border: '1px solid #555',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                />
                <div style={{ fontSize: '12px', marginTop: '4px', textTransform: 'capitalize' }}>
                  {frameName}
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* NEXT */}
        <button
          onClick={() => showSlide(activeIndex + 1)}
          style={navBtnStyle('right')}
        >
          ❯
        </button>

      </div>

      {/* ================= MODAL CAROUSEL ================= */}
      {isModalOpen && currentModalImage && (
        <div onClick={closeModal} style={modalOverlay}>
          <div 
            onClick={(e) => e.stopPropagation()} 
            style={{
              position: 'relative',
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Modal Title */}
            <div style={{
              position: 'absolute',
              top: '0.1px',
              left: 0,
              right: 0,
              textAlign: 'center',
              color: '#fff',
              fontSize: '24px',
              textTransform: 'capitalize',
              zIndex: 10,
              background: 'rgba(0,0,0,0.5)',
              padding: '10px'
            }}>
              {selectedFrame} Frame Collection ({modalActiveIndex + 1} of {modalImages.length})
            </div>

            {/* LEFT ARROW */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                showModalSlide(-1);
              }}
              style={modalNavBtnStyle('left')}
            >
              ❮
            </button>

            {/* MAIN IMAGE WITH FRAME */}
            <div style={{ position: 'relative', maxWidth: '145vw', maxHeight: '85vh' }}>
              <WatermarkedImage
                src={typeof currentModalImage === 'string' ? currentModalImage : currentModalImage?.image}
                watermark=""
                style={{
                  maxWidth: '100vw',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
              
                        </div>

            {/* RIGHT ARROW */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                showModalSlide(1);
              }}
              style={modalNavBtnStyle('right')}
            >
              ❯
            </button>

            {/* CLOSE BUTTON */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '20px',
                right: '30px',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                border: 'none',
                fontSize: '30px',
                cursor: 'pointer',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ✕
            </button>

            {/* ← ADD TO CART BUTTON in modal */}
            <div style={{
              position: 'absolute',
              bottom: '30px',
              left: 0,
              right: 0,
              textAlign: 'center',
              zIndex: 10
            }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(currentModalImage);
                }}
                className="add-to-cart-btn"
                style={addToCartBtnStyle}
              >
                🛒 Add to Cart - ${currentModalImage?.price || '0'}
              </button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
};

export default ArtworkCarousel;

/* ================= STYLES ================= */

const navBtnStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: '-40px',
  transform: 'translateY(-50%)',
  background: 'rgba(0,0,0,0.5)',
  border: 'none',
  color: '#fff',
  fontSize: '2rem',
  cursor: 'pointer',
  padding: '0.5rem',
  borderRadius: '50%',
  zIndex: 10
});

const modalNavBtnStyle = (side) => ({
  position: 'absolute',
  top: '50%',
  [side]: '30px',
  transform: 'translateY(-50%)',
  background: 'rgba(0,0,0,0.6)',
  border: 'none',
  color: '#fff',
  fontSize: '3rem',
  cursor: 'pointer',
  padding: '15px',
  borderRadius: '50%',
  zIndex: 10,
  transition: 'all 0.3s'
});

const modalOverlay = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.95)',
  zIndex: 1000
};

const addToCartBtnStyle = {
  background: '#ff4081',
  color: '#fff',
  padding: '0.7rem 1.5rem',
  borderRadius: '8px',
  border: 'none',
  fontWeight: 'bold',
  fontSize: '16px',
  cursor: 'pointer',
  transition: 'transform 0.2s, background 0.2s'
};